import { playgroundApi } from '../../common/api-client';
import {
  BaseMonthlyPolicyRow, BasePolicyRow, BaseSavingsInsuranceResponse, buildRatePayload,
} from '../_shared';
import { Beneficiary, InsuredUniversityFormValues } from './schema';

export const PRODUCT = 'UNIVERSITY_SAVINGS_INSURANCE';

export interface UniversityPolicyRow extends BasePolicyRow {
  withdrawal: number;
  policy_value_before_withdrawal?: number;
  policy_value_after_withdrawal?: number;
}

export interface UniversityMonthlyPolicyRow extends BaseMonthlyPolicyRow {
  withdrawal: number;
  policy_value_before_withdrawal?: number;
  policy_value_after_withdrawal?: number;
}

export interface ResponseBeneficiary {
  date_of_birth: string;
  start_date: string;
  duration_years: number;
  // In savings_capacity mode this is the FUNDED tuition (ideal x coverage),
  // not the ideal the client requested.
  annual_tuition: number;
}

export interface InsuredUniversityResponse
  extends BaseSavingsInsuranceResponse<UniversityPolicyRow, UniversityMonthlyPolicyRow> {
  terminal_excess: number;
  beneficiaries: ResponseBeneficiary[];
  // Fraction of the ideal tuition the premium funds (1.0 in goal_value mode).
  coverage_factor: number;
}

export interface SimulateParams {
  values: InsuredUniversityFormValues;
  beneficiaries: Beneficiary[];
}

function buildUniversityRatePayload(values: InsuredUniversityFormValues) {
  const {
    rate_mode: _rateMode,
    projection_rate: _projectionRate,
    projection_rate_schedule: _projectionRateSchedule,
    direct_contribution_amount,
    direct_contribution_month,
    ...rest
  } = values;
  const direct_contribution = direct_contribution_amount > 0
    ? { amount: direct_contribution_amount, kind: 'UNIQUE', month: direct_contribution_month }
    : undefined;
  return {
    ...rest,
    ...buildRatePayload(values),
    ...(direct_contribution ? { direct_contribution } : {}),
  };
}

export async function simulateInsuredUniversity({
  values,
  beneficiaries,
}: SimulateParams): Promise<InsuredUniversityResponse> {
  // beneficiaries is JSON-stringified: the backend parses it from a single query param string
  const payload = {
    product: PRODUCT,
    origin_process_id: `playground-${crypto.randomUUID().slice(0, 8)}`,
    ...buildUniversityRatePayload(values),
    beneficiaries: JSON.stringify(beneficiaries),
    debug: true,
  };
  const { data } = await playgroundApi.post<InsuredUniversityResponse>(
    'projection/',
    payload,
  );
  return data;
}

export function buildRequestPreview(
  values: InsuredUniversityFormValues,
  beneficiaries: Beneficiary[],
) {
  return {
    product: PRODUCT,
    origin_process_id: 'playground-xxxxxxxx',
    ...buildUniversityRatePayload(values),
    beneficiaries,
    debug: true,
  };
}
